import swal from 'sweetalert';

import service from '../../services/auth.service';
import { authTypes } from './types';

/**
 * Authenticates a user with username/password (local login).
 * On success stores `_auth_method_ = 'local'` in localStorage so that
 * the logout action knows it does NOT need to hit the SSO logout endpoint.
 *
 * @param {string} user - Username (trimmed before sending).
 * @param {string} pass - Plain-text password.
 */
const login = (user, pass) => {
    const username = user.trim();

    return (dispatch) => {
        service
            .login(username, pass)
            .then((result) => {
                const { token, refresh_token } = result.data;
                localStorage.setItem('_auth_method_', 'local');
                dispatch(newToken({ access_token: token, refresh: refresh_token }));
                delete result.data.token;
                delete result.data.refresh_token;
                dispatch(loginSuccess(result.data));
            })
            .catch((err) => dispatch(loginFail(err)));
    };
};

const refresh = () => {
    return (dispatch) => {
        service
            .tokenRefresh()
            .then((result) => dispatch(newToken(result.data)))
            .catch(() => dispatch(logOut()));
    };
};

/**
 * Completes the SSO login flow from the `/sso/validate/` route.
 * Calls the backend to exchange the SSO ticket (cookie) for ZINA BI tokens
 * and stores `_auth_method_ = 'sso'` in localStorage so logout is routed
 * through the SSO gateway instead of the local DRF endpoint.
 */
const token = () => {
    return (dispatch) => {
        dispatch({ type: authTypes.LOGIN });
        return service
            .token()
            .then((result) => {
                if (result && result.data) {
                    const { access_token, refresh } = result.data;
                    localStorage.setItem('_auth_method_', 'sso');
                    dispatch(newToken({ access_token, refresh }));
                    delete result.data.access_token;
                    delete result.data.refresh;
                    dispatch(loginSuccess(result.data.user));
                }

                return result;
            })
            .catch((error) => {
                return dispatch(loginFail(error));
            });
    };
};

const refresh_token = () => {
    return (dispatch) => {
        service
            .token_refresh()
            .then((result) => dispatch(newToken({ access_token: result.data.access })))
            .catch(() => dispatch(logOut()));
    };
};

const newToken = (payload) => ({
    type: authTypes.NEW_TOKEN,
    payload,
});

const loginSuccess = (response) => ({
    type: authTypes.LOGIN_SUCCESS,
    payload: response,
});

const refreshData = (response) => ({
    type: authTypes.LOGIN_REFRESH_DATA,
    payload: response,
});

const loginFail = (payload) => ({
    type: authTypes.LOGIN_FAIL,
    payload,
});

/**
 * Logs out the current user.
 *
 * - SSO sessions: dispatches LOGOUT and redirects to the SSO gateway logout
 *   endpoint so the gateway cookie is also invalidated.
 * - Local sessions: revokes the DRF authtoken server-side (fire-and-forget),
 *   then dispatches LOGOUT to clear the Redux store and localStorage.
 */
const logOut = () => (dispatch) => {
    // SSO sessions are terminated by the gateway; it issues its own redirect back
    if (localStorage.getItem('_auth_method_') === 'sso') {
        dispatch({ type: authTypes.LOGOUT });
        service.redirect_sso_logout();
        return;
    }

    // fire-and-forget: revoke the DRF authtoken server-side without delaying the UI/redirect
    service.logout().catch(() => {});
    dispatch({ type: authTypes.LOGOUT });
};

const getAccessStart = () => {
    return {
        type: authTypes.GET_ACCESS_START,
    };
};

const getAccessFailed = (error) => {
    return {
        type: authTypes.GET_ACCESS_FAIL,
        error,
    };
};

const getAccessSuccess = (data) => {
    return {
        type: authTypes.GET_ACCESS_SUCCESS,
        data,
    };
};

const getAccess = (data) => {
    return (dispatch) => {
        dispatch(getAccessStart());
        service
            .getAccess(data)
            .then((response) => {
                swal(
                    ` Welcome ${response.data.username}!`,
                    `Your acount was successfuly create.`,
                    'success'
                );
                dispatch(getAccessSuccess(response));
            })
            .catch((error) => dispatch(getAccessFailed(error)));
    };
};

const getProjectsStart = () => {
    return {
        type: authTypes.GET_PROJECT_LIST_OPTIONS_START,
    };
};

const getProjectsFail = (error) => {
    return {
        type: authTypes.GET_PROJECT_LIST_OPTIONS_FAIL,
        error,
    };
};

const getProjectsSuccess = (data) => {
    return {
        type: authTypes.GET_PROJECT_LIST_OPTIONS_SUCCESS,
        data,
    };
};

const fetchProjectListOptions = () => {
    return (dispatch) => {
        dispatch(getProjectsStart());
        service
            .getProjectList()
            .then((response) => dispatch(getProjectsSuccess(response)))
            .catch((error) => dispatch(getProjectsFail(error)));
    };
};

export const authActions = {
    token,
    refresh_token,
    login,
    logOut,
    refresh,
    getAccess,
    fetchProjectListOptions,
    refreshData,
};
